import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useState } from 'react';
import { ppdfConsentsQuery, useAxiosInstance, usePoQuery } from '@gov-nx/api';
import { useProcessControl } from '@gov-nx/core/hooks';
import type { Nullable, Optional } from '@gov-nx/core/types';
import { hasDefinedValues, toStringDate } from '@gov-nx/utils/common';
import { PageCode } from '../../definitions/codes';
import { usePpdfSouhlasyLocale } from '../usePpdfSouhlasyLocale';
import { FormInstance, prepareSubmitData } from './FormDefinitions';
import { MIN_CONSENTS_FOR_DOWNLOAD_ALL } from './constants';
import type {
	PpdfConsentsListControl,
	PpdfSouhlasySeznamContext,
} from './context.types';
import { usePpdfConsentsFilters } from './hooks/usePpdfConsentsFilters';
import { usePpdfDownloadAllConsents } from './hooks/usePpdfDownloadAllConsents';

const PageContext = createContext<Nullable<PpdfSouhlasySeznamContext>>(null);

interface Props {
	children: ReactNode;
}

export const PpdfSouhlasySeznamContextProvider = ({ children }: Props) => {
	const { t } = usePpdfSouhlasyLocale();
	const code = PageCode['ppdf-souhlasy'];
	const [ppdfConsentDetailId, setPpdfConsentDetailId] =
		useState<Optional<string>>(undefined);

	const { setControls, controls } = useProcessControl<PpdfConsentsListControl>({
		displayFilter: false,
		initialLoading: false,
	});

	const defaultValues = {
		text: undefined,
		od: undefined,
		do: undefined,
	};

	const formDefinition = FormInstance({
		code,
		defaultValues,
	});
	const ppdfConsentsFilters = usePpdfConsentsFilters({
		code,
		formMethods: formDefinition.formMethods,
		initialState: {
			...defaultValues,
			start: 0,
			pageSize: 10,
		},
	});
	const { filter } = ppdfConsentsFilters;
	const { axiosInstance } = useAxiosInstance();

	const ppdfDownloadAllConsents = usePpdfDownloadAllConsents({
		text: filter.text,
		od: filter.od ? toStringDate(filter.od) : undefined,
		do: filter.do ? toStringDate(filter.do) : undefined,
	});

	const query = usePoQuery({
		queryKey: ['ppdf-consent', ppdfConsentsFilters.filter],
		queryFn: async () => {
			setControls({ initialLoading: true });

			const requestData = prepareSubmitData(ppdfConsentsFilters.filter);
			return ppdfConsentsQuery(axiosInstance, requestData);
		},
		onSuccess: () => {
			setControls({ initialLoading: false });
		},
		retry: 0,
	});

	const handleSubmit = useCallback(async () => {
		ppdfConsentsFilters.submitFilters(formDefinition.formMethods.getValues());
	}, [formDefinition.formMethods, ppdfConsentsFilters]);
	const onSubmit = formDefinition.formMethods.handleSubmit(handleSubmit);

	return (
		<PageContext.Provider
			value={{
				isLoading: query.isLoading,
				query,
				data:
					query.data?.seznam?.map((item) => ({
						...item,
						nazevSpravceUdaju: item.spravceUdaju,
						nazevPrijemceUdaju: item.prijemceUdaju,
					})) ?? [],
				numberOfConsents: query.data?.pocet ?? 0,
				ppdfConsentDetailId,
				setPpdfConsentDetailId,
				controls,
				setControls,
				formDefinition,
				ppdfConsentsFilters,
				ppdfDownloadAllConsents,
				isDownloadButtonVisible: query.data?.pocet
					? query.data.pocet >= MIN_CONSENTS_FOR_DOWNLOAD_ALL
					: false,
				downloadButtonText: hasDefinedValues(filter, ['text', 'od', 'do'])
					? t('akce.stahnout-filtrovane-souhlasy.label')
					: t('akce.stahnout-vsechny-souhlasy.label'),
				onSubmit,
			}}>
			{children}
		</PageContext.Provider>
	);
};

export const usePpdfSouhlasySeznamContext = () => {
	const context = useContext(PageContext);
	if (!context) {
		throw new Error('Context must be used within a ContextProvider');
	}
	return context;
};
